import {
  LOCAL_STORAGE_KEYS,
  PRESERVE_ON_LOGOUT,
} from "@/shared/constants/localStorageKeys";

/**
 * localStorage is unavailable in some privacy modes and throws on quota. Every
 * access is guarded so a storage failure can never take the app down.
 */
const safeStorage = (): Storage | null => {
  try {
    if (typeof window === "undefined" || !window.localStorage) return null;
    return window.localStorage;
  } catch {
    return null;
  }
};

export const readStorage = (key: string): string | null => {
  const store = safeStorage();
  if (!store) return null;
  try {
    return store.getItem(key);
  } catch {
    return null;
  }
};

export const writeStorage = (key: string, value: string): void => {
  const store = safeStorage();
  if (!store) return;
  try {
    store.setItem(key, value);
  } catch {
    /* quota or private mode — non-fatal */
  }
};

export const removeStorage = (key: string): void => {
  const store = safeStorage();
  if (!store) return;
  try {
    store.removeItem(key);
  } catch {
    /* non-fatal */
  }
};

/** The bearer token used by the API client, or null when logged out. */
export const getAccessToken = (): string | null => {
  const raw = readStorage(LOCAL_STORAGE_KEYS.ACCESS_TOKEN);
  if (!raw) return null;
  // Historical writes were JSON-quoted; strip them defensively.
  return raw.replace(/^"|"$/g, "") || null;
};

export const setAccessToken = (token: string): void =>
  writeStorage(LOCAL_STORAGE_KEYS.ACCESS_TOKEN, token);

export const clearAccessToken = (): void =>
  removeStorage(LOCAL_STORAGE_KEYS.ACCESS_TOKEN);

/**
 * Wipe everything except the keys that legitimately outlive a session.
 * Web3Auth writes its own session keys, so a blanket clear is intentional —
 * it is what guarantees a clean logout.
 */
export const clearStorageOnLogout = (): void => {
  const store = safeStorage();
  if (!store) return;
  try {
    const preserved = PRESERVE_ON_LOGOUT.map(
      (key) => [key, store.getItem(key)] as const
    );
    store.clear();
    preserved.forEach(([key, value]) => {
      if (value !== null) store.setItem(key, value);
    });
  } catch {
    /* non-fatal */
  }
};
